"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";

export default function Home() {
  const router = useRouter();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => (prev < 90 ? prev + 5 : prev));
    }, 150);

    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (!token) return router.replace("/login");

      try {
        const res = await fetch("/api/auth", {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) return router.replace("/login");

        const data = await res.json();
        setProgress(100);

        setTimeout(() => {
          if (data.role === "user") {
            router.replace("/dashboard");
          } else {
            router.replace("/admin");
          }
        }, 2200); // 2.2 sec delay
      } catch (error) {
        console.error("Auth error:", error);
        router.replace("/login");
      }
    };

    checkAuth();
    return () => clearInterval(interval);
  }, [router]);

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-black overflow-hidden">
      {/* Glowing Animated Background Gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-red-900 opacity-20 blur-3xl animate-pulse z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ duration: 2 }}
      />

      {/* Neon Border and Progress Bar */}
      <motion.div
        className="absolute top-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-red-500 z-50 shadow-lg"
        initial={{ width: 0 }}
        animate={{ width: `${progress}%` }}
        transition={{ ease: "easeOut", duration: 0.4 }}
      />

      {/* Floating Particles (Optional FX) */}
      <motion.div
        className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.05 }}
        transition={{ delay: 1.2, duration: 2 }}
      >
        <div className="w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-red-500/10 animate-pulse blur-2xl" />
      </motion.div>

      {/* Main Animated Card Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="z-10"
      >
        <Card className="max-w-md mx-auto p-6 bg-white/5 border border-white/10 backdrop-blur-lg shadow-2xl rounded-2xl">
          <CardContent className="flex flex-col items-center gap-5">
            {/* Rotating Loader */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}
            >
              <Loader2 className="text-cyan-400 w-12 h-12 drop-shadow-lg" />
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold tracking-widest uppercase text-white drop-shadow-md"
            >
              Verifying Access...
            </motion.h1>

            {/* Subtext */}
            <p className="text-sm text-gray-300 text-center">
              Welcome to <span className="text-cyan-400 font-bold">Reachtopia</span>
              <br />
              The future of Smart Ad Management
            </p>

            {/* Credits */}
            <motion.p
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xs text-gray-400 mt-1"
            >
              Designed by <span className="text-pink-500">Ebani Tech</span> 🚀
            </motion.p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Ambient Floating Lights (UI Mood FX) */}
      <motion.div
        className="absolute bottom-10 left-10 w-24 h-24 rounded-full bg-blue-500 blur-2xl opacity-30 animate-pulse"
        animate={{ x: [0, 20, -20, 0], y: [0, 10, -10, 0] }}
        transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-20 right-20 w-24 h-24 rounded-full bg-red-500 blur-2xl opacity-30 animate-pulse"
        animate={{ x: [0, -20, 20, 0], y: [0, -10, 10, 0] }}
        transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
      />
    </div>
  );
}
